import React, { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import Header from './Header';
import SEO from './SEO';
import { findFlaggedIngredients } from '../utils/checkerMatcher';

const SITE_URL = 'https://www.useacnesafe.com';

export const checkerPageConfigs = {
  'acne-safe-ingredient-checker': {
    title: 'Acne Safe Ingredient Checker: Free Product Scan',
    description: 'Use the free Acne Safe ingredient checker to scan skincare, makeup, haircare, or sunscreen labels for ingredients commonly flagged for acne-prone skin.',
    eyebrow: 'Free ingredient screening tool',
    heading: 'Acne Safe Ingredient Checker',
    intro: 'Paste a complete product label to screen it against our current list of ingredients commonly flagged by acne-prone users.',
    primaryKeyword: 'acne safe ingredient checker',
  },
  'pore-clogging-ingredient-checker': {
    title: 'Pore Clogging Ingredient Checker: Free Scan | Acne Safe',
    description: 'Use this free pore-clogging ingredient checker to scan a complete cosmetic ingredient list and review ingredients commonly associated with comedogenicity.',
    heading: 'Pore Clogging Ingredient Checker',
    intro: 'Paste any skincare, makeup, haircare, or sunscreen ingredient list to check for ingredients commonly described as pore-clogging or comedogenic.',
    primaryKeyword: 'pore-clogging ingredient checker',
  },
  'comedogenic-ingredient-checker': {
    title: 'Comedogenic Ingredient Checker: Free INCI Scan | Acne Safe',
    description: 'Paste a cosmetic INCI list into this free comedogenic ingredient checker. See possible matches plus clear limitations about ingredient-level ratings.',
    eyebrow: 'Plain-language INCI screening',
    heading: 'Comedogenic Ingredient Checker',
    intro: 'Paste the full INCI list to identify names in our screening database and learn what the result can—and cannot—tell you.',
    primaryKeyword: 'comedogenic ingredient checker',
  },
};

const checkerFaqs = [
  {
    question: 'Does a flagged ingredient guarantee that a product will clog my pores?',
    answer: 'No. A match is a screening signal, not a product verdict. Concentration, formulation, exposure, and individual skin response can all change how a finished product behaves.',
  },
  {
    question: 'What ingredient list should I paste?',
    answer: 'Use the complete, current INCI list from the product packaging or the manufacturer’s website. Partial or outdated lists can produce incomplete results.',
  },
  {
    question: 'What does “no matches found” mean?',
    answer: 'It means no names matched this database. It does not prove that the product is non-comedogenic, irritation-free, or suitable for every person.',
  },
  {
    question: 'Is this medical advice?',
    answer: 'No. Acne Safe provides educational screening information and does not diagnose acne or replace advice from a dermatologist or other qualified clinician.',
  },
];

function CheckerLandingPage({ pageKey }) {
  const config = checkerPageConfigs[pageKey];
  const isPoreChecker = pageKey === 'pore-clogging-ingredient-checker';
  const [label, setLabel] = useState('');
  const [hasScanned, setHasScanned] = useState(false);
  const matches = useMemo(() => (hasScanned ? findFlaggedIngredients(label) : []), [hasScanned, label]);

  if (!config) return <Navigate to="/" replace />;

  const canonicalUrl = `${SITE_URL}/${pageKey}`;
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: config.heading,
      url: canonicalUrl,
      applicationCategory: 'HealthApplication',
      operatingSystem: 'Web',
      isAccessibleForFree: true,
      description: config.description,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      name: `${config.heading} Frequently Asked Questions`,
      mainEntity: checkerFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      name: `${config.heading} Breadcrumbs`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: config.heading, item: canonicalUrl },
      ],
    },
  ];

  const handleScan = () => {
    setHasScanned(true);
  };

  return (
    <div className={`App checker-page${isPoreChecker ? ' checker-page--pore' : ''}`}>
      <SEO title={config.title} description={config.description} url={canonicalUrl} />
      {structuredData.map((schema) => (
        <script
          key={schema['@type']}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      <Header />

      <main className="checker-main">
        <section className="checker-hero">
          {!isPoreChecker && <p className="checker-eyebrow">{config.eyebrow}</p>}
          <h1>
            {isPoreChecker ? (
              <>
                <span>Pore Clogging</span>
                <span className="checker-heading-accent">Ingredient Checker</span>
              </>
            ) : config.heading}
          </h1>
          <p>{config.intro}</p>
        </section>

        <section
          className="checker-tool"
          aria-labelledby={isPoreChecker ? undefined : 'checker-tool-title'}
          aria-label={isPoreChecker ? 'Ingredient checker' : undefined}
        >
          {!isPoreChecker && (
            <div className="checker-tool-heading">
              <h2 id="checker-tool-title">Paste the complete ingredient list</h2>
              <p>Commas, semicolons, and line breaks all work.</p>
            </div>
          )}
          <textarea
            id="product-ingredients"
            className="ingredients-textarea"
            value={label}
            onChange={(event) => {
              setLabel(event.target.value);
              setHasScanned(false);
            }}
            rows="8"
            aria-label="Product ingredient list"
            placeholder={isPoreChecker ? 'Paste your ingredients here' : 'Example: Water, Glycerin, Niacinamide, Isopropyl Myristate…'}
          />
          <button className="analyze-button" type="button" onClick={handleScan} disabled={!label.trim()}>
            {isPoreChecker ? 'Analyze ingredients' : 'Check ingredients'}
          </button>

          {hasScanned && (
            <div className={`checker-results ${matches.length ? 'has-matches' : 'no-matches'}`} aria-live="polite">
              <h3>{matches.length ? `${matches.length} possible match${matches.length === 1 ? '' : 'es'} found` : 'No database matches found'}</h3>
              {matches.length ? (
                <ul>
                  {matches.map((ingredient) => (
                    <li key={ingredient.slug}>
                      <Link to={`/ingredients/${ingredient.slug}`}>{ingredient.name}</Link>
                      <span>{ingredient.category}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No match is not a guarantee that the finished product will work for your skin.</p>
              )}
            </div>
          )}
        </section>

        <section className="checker-explainer">
          <div>
            <h2>{isPoreChecker ? 'What the checker looks for' : `How this ${config.primaryKeyword} works`}</h2>
            <p>We compare the names in your label with a curated list of cosmetic ingredients and known aliases. Matches are shown individually so you can investigate them instead of relying on an unexplained product score.</p>
          </div>
          <div>
            <h2>{isPoreChecker ? 'A match is not a verdict' : 'Read the result with context'}</h2>
            <p>Ingredient-level comedogenicity evidence is limited and often historical. A raw ingredient tested under controlled conditions is not the same as a modern finished formula.</p>
          </div>
        </section>

        <section className="checker-steps">
          <h2>How to check a product accurately</h2>
          <ol>
            <li><strong>Use the current label.</strong> Formulas change, so prefer the packaging in your hand or the manufacturer’s current page.</li>
            <li><strong>Paste every ingredient.</strong> A partial list can hide relevant names and aliases.</li>
            <li><strong>Treat matches as questions.</strong> Consider ingredient order, product type, contact time, and your own history.</li>
            <li><strong>Patch test when appropriate.</strong> Stop using a product and seek qualified care if you experience a concerning reaction.</li>
          </ol>
        </section>

        <section className="checker-faq">
          <h2>Frequently asked questions</h2>
          {checkerFaqs.map((faq) => (
            <article key={faq.question}>
              <h3>{faq.question}</h3>
              <p>{faq.answer}</p>
            </article>
          ))}
        </section>

        <nav className="checker-related" aria-label="Related ingredient checkers">
          <h2>Related tools and guides</h2>
          <div>
            {Object.entries(checkerPageConfigs)
              .filter(([slug]) => slug !== pageKey)
              .map(([slug, page]) => <Link key={slug} to={`/${slug}`}>{page.heading}</Link>)}
            <Link to="/ingredients">Browse flagged ingredients</Link>
            <Link to="/products">Search analyzed products</Link>
          </div>
        </nav>
      </main>
    </div>
  );
}

export default CheckerLandingPage;
